// Pure helpers of the Google Drive connectors of the office's library (catalogue id `gdrive`:
// server/connectors/plugins/gdrive.js, server/modules/drive.js) in the web app — the « Google
// Drive » windows of « Connecteurs du bureau » (myconnectors/drive.js) and the « Application
// Google » block of Administration → Connecteurs (admin/drive.js): the office's Google app as the
// server shows it (never a secret: their last 4 characters), a connector's connection and transfers
// (GET …/library/:id/google/status), the administrators' overview (GET /api/storage/google/status),
// the states and their badges, sizes, what is still on the server, the only Google address « Se
// connecter avec Google » may open, a folder's link, the office's authorised domain for Google's
// Branding page, and the return from Google (#/connectors/<id>?drive=…). No DOM.
// Tested in test/ui-drive.test.js.
import { t } from '../../i18n/fr.js';
import { isSafeUrlAttr } from '../../dom.js';
import { formatNumber } from '../../format.js';
import { LIBRARY_KEY_RE } from '../connectorpolicy.js';
import './strings.js';

/** The catalogue id of a Google Drive connector. */
export const GDRIVE_ID = 'gdrive';
/** The office's Google app and the administrators' overview (server/modules/drive.js). */
export const DRIVE_BASE = '/api/storage/google';
/** A connector's access: drive.file (the folder picked, the files the office makes) or drive (everything). */
export const DRIVE_MODES = Object.freeze(['limited', 'full']);
/** How Google's redirect ended (#/connectors/<id>?drive=…, the server's callback). */
export const DRIVE_OUTCOMES = Object.freeze(['ok', 'error', 'expired', 'denied', 'login']);
/** The `type` of the message the picker page posts to the page that opened it (server PICKER_MESSAGE). */
export const PICKER_MESSAGE = 'maeck-drive-picker';
/** The only address « Se connecter avec Google » may open: Google's consent page. */
export const GOOGLE_AUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth';
/** The folder the office creates at the root of « Mon Drive » for the storage connector without one (server OFFICE_FOLDER). */
export const OFFICE_FOLDER = 'Maeck Office';
/** A Drive file or folder id (server isDriveId). */
const DRIVE_ID_RE = /^[A-Za-z0-9_-]{1,200}$/;
/** Owners of the office's files the transfers name (server sweeps). */
const OWNER_RE = /^[a-z][a-z0-9_-]{0,30}$/i;
/**
 * Public suffixes of more than one label (and hosting platforms of the Public Suffix List) under
 * which the registrable domain keeps one more label: « office.up.railway.app » is its own.
 */
const MULTI_SUFFIXES = new Set([
  'co.uk', 'org.uk', 'me.uk', 'ac.uk', 'gov.uk', 'ltd.uk', 'plc.uk', 'com.au', 'net.au', 'org.au', 'co.nz', 'co.jp', 'co.za', 'co.in',
  'com.br', 'com.mx', 'com.ar', 'com.tr', 'com.sg', 'com.hk', 'com.cn', 'gouv.fr', 'asso.fr', 'com.fr', 'qc.ca',
  'up.railway.app', 'vercel.app', 'herokuapp.com', 'onrender.com', 'fly.dev', 'netlify.app', 'pages.dev', 'workers.dev', 'github.io',
  'ngrok-free.app', 'ngrok.app', 'ngrok.io', 'web.app', 'firebaseapp.com', 'appspot.com', 'azurewebsites.net', 'trycloudflare.com',
  'replit.app', 'glitch.me', 'deno.dev', 'koyeb.app', 'ondigitalocean.app',
]);

// eslint-disable-next-line no-control-regex
const str = (v, max = 500) => (typeof v === 'string' ? v.replace(/[\u0000-\u001f\u007f]/g, '').trim().slice(0, max) : '');
const num = (v) => (Number.isFinite(Number(v)) && Number(v) > 0 ? Number(v) : 0);
const isObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const dateOf = (v) => str(v, 40) || null;

/**
 * Normalises the office's Google app (GET|PUT|DELETE /api/storage/google/app, or `app` of
 * …/status): never a secret, their last characters at most.
 * @param {unknown} res
 * @returns {{configured: boolean, clientId: string, secretHint: string, apiKeyHint: string, appId: string,
 *   appIdFromClient: boolean, redirectUrl: string, privacyUrl: string, homeUrl: string, pickerReady: boolean,
 *   updatedAt: string|null, connectorsCleared: number}} appId: the project number of Google's picker
 *   (appIdFromClient: read from the client id); connectorsCleared: the Google Drive connectors
 *   disconnected by another client id or « Retirer »
 */
export function driveAppOf(res) {
  const r = isObject(res) ? res : {};
  const appId = /^\d+$/.test(str(r.appId, 20)) ? str(r.appId, 20) : '';
  return {
    configured: r.configured === true,
    clientId: str(r.clientId, 200),
    secretHint: str(r.secretHint, 20),
    apiKeyHint: str(r.apiKeyHint, 20),
    appId,
    appIdFromClient: Boolean(appId) && r.appIdFromClient === true,
    redirectUrl: str(r.redirectUrl, 1000),
    privacyUrl: str(r.privacyUrl, 1000),
    homeUrl: str(r.homeUrl, 1000),
    pickerReady: r.pickerReady === true,
    updatedAt: dateOf(r.updatedAt),
    connectorsCleared: Math.floor(num(r.connectorsCleared)),
  };
}

/** {files, bytes} of a count the server sends (missing or broken: zeros). */
function countOf(v) {
  return { files: Math.floor(num(v?.files)), bytes: num(v?.bytes) };
}

/**
 * A folder as the server sends it ({id, name, url}), or null.
 * @param {unknown} f
 */
export function driveFolderOf(f) {
  if (!isObject(f) || !DRIVE_ID_RE.test(String(f.id ?? ''))) return null;
  return { id: String(f.id), name: str(f.name, 250), url: str(f.url, 1000) };
}

/**
 * A Google account as the server sends it ({email, name}), or null.
 * @param {unknown} a
 */
export function driveAccountOf(a) {
  if (!isObject(a)) return null;
  const email = str(a.email, 320);
  const name = str(a.name, 200);
  return email || name ? { email, name } : null;
}

/**
 * Normalises a Google Drive connector's connection and transfers (GET /api/connectors/library/:id/
 * google/status, its PUT answers, an item of /api/storage/google/status `connectors`).
 * @param {unknown} res
 * @returns {{id: string, label: string, connected: boolean, needsReconnect: boolean, mode: 'limited'|'full'|null,
 *   account: {email: string, name: string}|null, connectedAt: string|null, folder: {id, name, url}|null, created: boolean,
 *   storage: boolean, ready: boolean, paused: boolean, pending: Record<string, {files: number, bytes: number}>,
 *   done: {files: number, bytes: number}, sweeping: boolean, lastSweepAt: string|null, lastError: string|null,
 *   lastErrorAt: string|null, errors: Array<{owner: string, message: string, at: string|null}>,
 *   quota: {usage: number, limit: number|null}|null, trash: number}}
 *   storage: it stores the office's files (one connector at most); pending: what the office still
 *   holds on its server, per owner (the storage connector's; zeros otherwise); done: the office's
 *   files it holds
 */
export function driveConnectorOf(res) {
  const r = isObject(res) ? res : {};
  const pending = {};
  if (isObject(r.pending)) {
    for (const [owner, v] of Object.entries(r.pending)) if (OWNER_RE.test(owner)) pending[owner] = countOf(v);
  }
  const q = isObject(r.quota) ? r.quota : null;
  const id = String(r.id ?? '');
  return {
    id: LIBRARY_KEY_RE.test(id) ? id : '',
    label: str(r.label, 80),
    connected: r.connected === true,
    needsReconnect: r.needsReconnect === true,
    mode: DRIVE_MODES.includes(r.mode) ? r.mode : null,
    account: driveAccountOf(r.account),
    connectedAt: dateOf(r.connectedAt),
    folder: driveFolderOf(r.folder),
    created: r.created === true,
    storage: r.storage === true,
    ready: r.ready === true,
    paused: r.paused === true,
    pending,
    done: countOf(r.done),
    sweeping: r.sweeping === true,
    lastSweepAt: dateOf(r.lastSweepAt),
    lastError: str(r.lastError, 600) || null,
    lastErrorAt: dateOf(r.lastErrorAt),
    errors: (Array.isArray(r.errors) ? r.errors : []).filter(isObject).slice(0, 20)
      .map((e) => ({ owner: str(e.owner, 40), message: str(e.message, 600), at: dateOf(e.at) })).filter((e) => e.message),
    quota: q ? { usage: num(q.usage), limit: num(q.limit) || null } : null,
    trash: Math.floor(num(r.trash)),
  };
}

/**
 * Normalises the administrators' overview (GET /api/storage/google/status): the app, the
 * connector storing the office's files (`storage`: its id, or null), every Google Drive connector
 * of the library, the server's disk.
 * @param {unknown} res
 * @returns {{app: object, storage: string|null, connectors: object[], disk: {total: number, free: number, used: number}|null}}
 */
export function driveStatusOf(res) {
  const r = isObject(res) ? res : {};
  const d = isObject(r.disk) ? r.disk : null;
  const connectors = (Array.isArray(r.connectors) ? r.connectors : []).map(driveConnectorOf).filter((c) => c.id);
  const storage = typeof r.storage === 'string' && LIBRARY_KEY_RE.test(r.storage) ? r.storage : null;
  return {
    app: driveAppOf(r.app),
    storage,
    connectors: connectors.map((c) => (storage && c.id === storage && !c.storage ? { ...c, storage: true } : c)),
    disk: d && num(d.total) ? { total: num(d.total), free: num(d.free), used: num(d.used) } : null,
  };
}

/**
 * Where a connector stands: 'off' (never connected, or its access erased), 'reconnect' (Google
 * refused it: to connect again), 'paused' (connected, its transfers paused) or 'connected'.
 * @param {object|null} view driveConnectorOf()
 */
export function driveState(view) {
  if (view?.needsReconnect) return 'reconnect';
  if (!view?.connected) return 'off';
  return view.paused ? 'paused' : 'connected';
}

/**
 * True for a connector connected but without its folder yet: its agents can do nothing there, the
 * next step is « Choisir le dossier » (never « all done »).
 * @param {object|null} view driveConnectorOf()
 */
export function driveNeedsFolder(view) {
  return driveState(view) === 'connected' && !view.folder;
}

/** The badge of a connector's state ({key, tone}): DOSSIER À CHOISIR (amber) while connected without its folder. */
export function driveStateBadge(view) {
  if (driveNeedsFolder(view)) return { key: 'pc.drive.state.needsFolder', tone: 'amber' };
  const state = driveState(view);
  const tone = { connected: 'success', paused: 'neutral', reconnect: 'amber', off: 'neutral' }[state];
  return { key: `pc.drive.state.${state}`, tone };
}

/**
 * The badge of the office's app: À RÉGLER, CLÉ API MANQUANTE (set without the key of Google's folder
 * picker: « Limité à un dossier » cannot choose its folder) or PRÊTE.
 */
export function driveAppBadge(app) {
  if (!app?.configured) return { key: 'adm.drive.notReady', tone: 'amber' };
  return app.pickerReady ? { key: 'adm.drive.ready', tone: 'success' } : { key: 'adm.drive.noKey', tone: 'amber' };
}

/** What the office still holds on its server, every owner together: {files, bytes}. */
export function pendingOf(view) {
  let files = 0;
  let bytes = 0;
  for (const v of Object.values(view?.pending || {})) {
    files += num(v?.files);
    bytes += num(v?.bytes);
  }
  return { files, bytes };
}

/** "512 o", "3,4 Mo", "18,2 Go", "1,2 To" (1024 steps; no « ,0 »). */
export function driveBytes(bytes) {
  let v = num(bytes);
  const units = ['o', 'Ko', 'Mo', 'Go', 'To'];
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  const decimals = i === 0 ? 0 : (v < (i >= 3 ? 100 : 10) ? 1 : 0);
  return `${formatNumber(v, decimals).replace(/,0$/, '')} ${units[i]}`;
}

/** « Encore sur le serveur : 1 234 fichiers, 18,2 Go » or « Tout est dans le Drive ». */
export function pendingText(view) {
  const p = pendingOf(view);
  return p.files ? t('pc.drive.pending', { n: p.files, files: formatNumber(p.files), size: driveBytes(p.bytes) }) : t('pc.drive.allInDrive');
}

/** « Fichiers du bureau rangés ici : 12 fichiers, 3,4 Go », or '' when it holds none. */
export function doneText(view) {
  const d = view?.done;
  return d?.files ? t('pc.drive.done', { n: d.files, files: formatNumber(d.files), size: driveBytes(d.bytes) }) : '';
}

/** « 4,2 Go utilisés sur 15 Go », « 4,2 Go utilisés (sans limite) », or '' when unknown. */
export function quotaText(quota) {
  if (!quota) return '';
  return quota.limit
    ? t('pc.drive.quotaOf', { used: driveBytes(quota.usage), limit: driveBytes(quota.limit) })
    : t('pc.drive.quotaNoLimit', { used: driveBytes(quota.usage) });
}

/** « marie@exemple.com (Marie Martin) », the e-mail alone, the name alone, or ''. */
export function accountText(account) {
  if (!account) return '';
  if (account.email && account.name && account.name !== account.email) return `${account.email} (${account.name})`;
  return account.email || account.name || '';
}

/**
 * The details line of a Google Drive connector for its managers (its plugin settings): « marie@
 * exemple.com · limité à un dossier · dossier « ECOM USA » » ('' without settings).
 * @param {{mode?: string, account?: object|null, folder?: object|null}|null} plugin
 */
export function driveDetails(plugin) {
  if (!plugin) return '';
  const account = plugin.account?.email || plugin.account?.name || '';
  return [
    account,
    DRIVE_MODES.includes(plugin.mode) ? t(`pc.drive.modeShort.${plugin.mode}`) : '',
    account || plugin.folder ? (plugin.folder ? t('pc.drive.folderIs', { name: plugin.folder.name || plugin.folder.id }) : t('pc.drive.folderNone')) : '',
  ].filter(Boolean).join(' · ');
}

/**
 * The address the server gave for « Se connecter avec Google » when it is Google's consent page
 * (https, accounts.google.com, /o/oauth2/v2/auth, no credentials, port nor fragment), else ''.
 * @param {unknown} url
 */
export function safeGoogleUrl(url) {
  let u;
  try { u = new URL(String(url ?? '')); } catch { return ''; }
  const ok = `${u.origin}${u.pathname}` === GOOGLE_AUTH_URL && !u.username && !u.password && !u.port && !u.hash;
  return ok && isSafeUrlAttr('href', u.href) ? u.href : '';
}

/**
 * « Ouvrir dans Drive » of a folder: its address when it is Google Drive's (https), else the one
 * built from its id, else ''.
 * @param {{id?: string, url?: string}|null} folder
 */
export function driveFolderLink(folder) {
  try {
    const u = new URL(String(folder?.url ?? ''));
    if (u.protocol === 'https:' && u.hostname === 'drive.google.com' && !u.username && !u.password && !u.port && isSafeUrlAttr('href', u.href)) return u.href;
  } catch { /* built from the id */ }
  const id = String(folder?.id ?? '');
  return DRIVE_ID_RE.test(id) ? `https://drive.google.com/drive/folders/${encodeURIComponent(id)}` : '';
}

/** The origin of an address ('' when it has none). */
export function originOf(url) {
  try {
    const o = new URL(String(url ?? '')).origin;
    return o === 'null' ? '' : o;
  } catch {
    return '';
  }
}

/** The host of an address ('' when it has none). */
export function hostOf(url) {
  try { return new URL(String(url ?? '')).hostname.toLowerCase(); } catch { return ''; }
}

/**
 * The domain to declare in Google's « Branding » → « Domaines autorisés » for the office's host:
 * its registrable domain (« office.maeck.xyz » → « maeck.xyz »; on a hosting platform's shared
 * suffix, the office's own name: « bureau.up.railway.app »). '' for an address, localhost or a
 * single label (Google refuses them).
 * @param {string} host
 */
export function registrableDomain(host) {
  const h = String(host ?? '').trim().toLowerCase().replace(/\.$/, '');
  if (!h || h.includes(':') || h.includes('[') || /^[\d.]+$/.test(h) || !h.includes('.') || !/^[a-z0-9.-]+$/.test(h)) return '';
  const labels = h.split('.');
  if (labels.some((l) => !l)) return '';
  if (labels[labels.length - 1] === 'localhost') return '';
  // The longest known suffix of several labels first.
  for (let i = 0; i < labels.length - 1; i++) {
    if (MULTI_SUFFIXES.has(labels.slice(i + 1).join('.'))) return labels.slice(i).join('.');
  }
  return labels.slice(-2).join('.');
}

/**
 * Back from Google (« Se connecter avec Google »): #/connectors[/<id>]?drive=<outcome> →
 * {outcome, id} (id null when the address names none), else null.
 * @param {unknown} hash location.hash
 * @returns {{outcome: string, id: string|null}|null}
 */
export function driveReturnOf(hash) {
  const raw = String(hash ?? '').replace(/^#\/?/, '');
  const [path, query = ''] = raw.split('?');
  const parts = path.split('/').filter(Boolean);
  if (parts[0] !== 'connectors') return null;
  const outcome = new URLSearchParams(query).get('drive');
  if (!DRIVE_OUTCOMES.includes(outcome)) return null;
  return { outcome, id: parts[1] && LIBRARY_KEY_RE.test(parts[1]) ? parts[1] : null };
}

/**
 * The toast of a return from Google: {kind: 'success'|'error'|'warn', text} — the next step while
 * it has no folder yet, else that it is connected again.
 * @param {string} outcome DRIVE_OUTCOMES
 * @param {{label?: string, lastError?: string, plugin?: object|null}|null} [conn] the connector, as read again
 */
export function driveReturnToast(outcome, conn = null) {
  if (outcome === 'ok') {
    if (!conn) return { kind: 'success', text: t('pc.drive.return.okNoConnector') };
    if (conn.plugin?.folder) return { kind: 'success', text: t('pc.drive.return.okAgain', { name: conn.label }) };
    if (conn.plugin && 'folder' in conn.plugin) return { kind: 'success', text: t('pc.drive.return.okPick') };
    return { kind: 'success', text: t('pc.drive.return.ok', { name: conn.label }) };
  }
  if (outcome === 'error') return { kind: 'error', text: conn?.lastError || t('pc.drive.return.error') };
  return { kind: 'warn', text: t(`pc.drive.return.${DRIVE_OUTCOMES.includes(outcome) ? outcome : 'expired'}`) };
}
